import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TableCard } from "@/components/application/table/table";
import { CHART_COLORS, QUALITY_BAND_COLORS, QUALITY_BAND_THRESHOLDS, type TrendDataPoint } from "../flag-qa-types";
import { formatTimeToAction } from "../flag-qa-utils";

interface FlagQaTrendChartProps {
  data: TrendDataPoint[];
  minFlagsThreshold: number;
  onMinFlagsThresholdChange: (value: number) => void;
}

// Custom dot component that colors based on quality band
function QualityDot(props: { cx?: number; cy?: number; payload?: TrendDataPoint }) {
  const { cx, cy, payload } = props;

  if (!cx || !cy || !payload) return null;

  const color = QUALITY_BAND_COLORS[payload.qualityBand];

  return <circle cx={cx} cy={cy} r={6} fill={color} stroke={CHART_COLORS.tooltipBg} strokeWidth={2} />;
}

// Custom tooltip
function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: TrendDataPoint }> }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
      <div className="font-medium text-primary mb-2">{data.period}</div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-tertiary">F1 Score:</span>
          <span className="font-medium" style={{ color: QUALITY_BAND_COLORS[data.qualityBand] }}>
            {data.f1.toFixed(1)}%
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-tertiary">Total Flags:</span>
          <span className="text-primary">{data.totalFlags}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-tertiary">Reviewed:</span>
          <span className="text-primary">{data.reviewedFlags}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-tertiary">Avg. Time to Action:</span>
          <span className="text-primary">{formatTimeToAction(data.avgTimeToActionHours)}</span>
        </div>
      </div>
    </div>
  );
}

export function FlagQaTrendChart({ data, minFlagsThreshold, onMinFlagsThresholdChange }: FlagQaTrendChartProps) {
  // Filter data based on minimum flags threshold
  const filteredData = data.filter((d) => d.totalFlags >= minFlagsThreshold);

  // Calculate max flags for slider
  const maxFlags = Math.max(...data.map((d) => d.totalFlags), 10);

  return (
    <div className="flex flex-col gap-4">
      {/* Threshold slider */}
      <TableCard.Root className="p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-secondary whitespace-nowrap">Min. Flags per Period:</label>
          <input
            type="range"
            min={0}
            max={Math.min(maxFlags, 100)}
            value={minFlagsThreshold}
            onChange={(e) => onMinFlagsThresholdChange(Number(e.target.value))}
            className="flex-1"
          />
          <span className="text-sm text-primary w-12 text-right">{minFlagsThreshold}</span>
        </div>
        <div className="text-xs text-tertiary mt-2">
          Showing {filteredData.length} of {data.length} periods
        </div>
      </TableCard.Root>

      {/* Chart */}
      <TableCard.Root className="p-4">
        <h3 className="text-lg font-semibold text-primary mb-4">F1 Score Trend</h3>

        {filteredData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-tertiary">
            No data matches the current threshold. Try lowering the minimum flags filter.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
              <XAxis
                dataKey="period"
                stroke={CHART_COLORS.axis}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                stroke={CHART_COLORS.axis}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Quality band threshold lines */}
              <ReferenceLine
                y={QUALITY_BAND_THRESHOLDS.low}
                stroke={QUALITY_BAND_COLORS.low}
                strokeDasharray="5 5"
                label={{
                  value: "Low",
                  position: "right",
                  fill: QUALITY_BAND_COLORS.low,
                  fontSize: 10,
                }}
              />
              <ReferenceLine
                y={QUALITY_BAND_THRESHOLDS.moderate}
                stroke={QUALITY_BAND_COLORS.moderate}
                strokeDasharray="5 5"
                label={{
                  value: "High",
                  position: "right",
                  fill: QUALITY_BAND_COLORS.high,
                  fontSize: 10,
                }}
              />

              <Line
                type="monotone"
                dataKey="f1"
                stroke="#6b7280"
                strokeWidth={2}
                dot={<QualityDot />}
                activeDot={{ r: 8, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* Legend */}
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: QUALITY_BAND_COLORS.low }} />
            <span className="text-xs text-tertiary">Low (&lt;30%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: QUALITY_BAND_COLORS.moderate }} />
            <span className="text-xs text-tertiary">Moderate (30-70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: QUALITY_BAND_COLORS.high }} />
            <span className="text-xs text-tertiary">High (&gt;70%)</span>
          </div>
        </div>
      </TableCard.Root>

      {/* Trend data table */}
      <TableCard.Root className="p-4">
        <h3 className="text-lg font-semibold text-primary mb-4">Period Details</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-tertiary font-medium">Period</th>
                <th className="text-right py-2 px-3 text-tertiary font-medium">Total Flags</th>
                <th className="text-right py-2 px-3 text-tertiary font-medium">Reviewed</th>
                <th className="text-right py-2 px-3 text-tertiary font-medium">F1 Score</th>
                <th className="text-right py-2 px-3 text-tertiary font-medium">Avg. Time to Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row) => (
                <tr key={row.period} className="border-b border-gray-800">
                  <td className="py-2 px-3 text-primary">{row.period}</td>
                  <td className="text-right py-2 px-3 text-primary">{row.totalFlags}</td>
                  <td className="text-right py-2 px-3 text-primary">
                    {row.reviewedFlags}
                    <span className="text-tertiary ml-1">
                      ({row.totalFlags > 0 ? ((row.reviewedFlags / row.totalFlags) * 100).toFixed(0) : 0}%)
                    </span>
                  </td>
                  <td className="text-right py-2 px-3">
                    <span style={{ color: QUALITY_BAND_COLORS[row.qualityBand] }}>{row.f1.toFixed(1)}%</span>
                  </td>
                  <td className="text-right py-2 px-3 text-primary">{formatTimeToAction(row.avgTimeToActionHours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCard.Root>
    </div>
  );
}
